import React, { useEffect, useMemo } from 'react';
import { Tabs } from '@hills-design-system/components';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useContentstackPage } from '../../hooks/useContentstack';
import { CmsComponentRenderer } from '../../components/CmsComponentRenderer/CmsComponentRenderer';

const DEFAULT_TAB = 'logos';

const TAB_SLUGS: Record<string, string> = {
  logos: 'digital-toolkit-logos',
  'adopter-handouts': 'digital-toolkit-adopter-handouts',
};

const TabContent: React.FC<{ slug: string }> = ({ slug }) => {
  const { t } = useTranslation();
  const { data: pageData, loading, error } = useContentstackPage(slug);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t(TranslationKeyEnum.LoadingPage)}</p>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return null;
  }

  return <CmsComponentRenderer components={pageData.components ?? []} />;
};

const DigitalToolkit: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = React.useState(tabParam ?? DEFAULT_TAB);
  const { t, locale } = useTranslation();
  const isCanadian = locale.toLowerCase().endsWith('-ca');

  const tabItems = useMemo(() => {
    const items = [{ id: 'logos', label: t(TranslationKeyEnum.Logos) }];
    if (isCanadian) {
      items.push({ id: 'adopter-handouts', label: t(TranslationKeyEnum.AdopterHandouts) });
    }
    return items;
  }, [isCanadian, t]);

  useEffect(() => {
    const validTabIds = tabItems.map((item) => item.id);
    if (tabParam && validTabIds.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    } else if (!tabParam || !validTabIds.includes(tabParam)) {
      setActiveTab(DEFAULT_TAB);
      setSearchParams({ tab: DEFAULT_TAB }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam, tabItems]);

  if (!isCanadian) {
    return <Navigate to="/" replace />;
  }

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const renderDescription = () => {
    const descriptionText = t(TranslationKeyEnum.DigitalToolkitDescription);
    const match = descriptionText.match(/^(.*)\{(.+?)\}(.*)$/s);

    if (match) {
      const [, before, linkText, after] = match;
      return (
        <>
          {before}
          <Link to={t(TranslationKeyEnum.DigitalToolkitTerms)} className="underline text-gray-925">
            {linkText}
          </Link>
          {after}
        </>
      );
    }

    return descriptionText;
  };

  return (
    <main className="flex-grow px-20 pb-16 pt-10">
      {/* Page Header */}
      <div className="pb-12 flex gap-6 flex-col">
        <h1 className="text-heading-400 leading-heading font-bold tracking-[-3%] text-gray-925">
          {t(TranslationKeyEnum.DigitalToolkit)}
        </h1>
        <p className="text-body-125 leading-body text-gray-925 font-medium max-w-[601px]">
          {renderDescription()}
        </p>
      </div>

      {/* Tabs */}
      <Tabs items={tabItems} activeTab={activeTab} onChange={handleTabChange} />

      {/* Content — render all tabs but hide inactive to avoid re-fetching */}
      {tabItems.map((tab) => (
        <div
          key={tab.id}
          className={`py-8 flex flex-col ${activeTab === tab.id ? '' : 'hidden'}`}
        >
          <TabContent slug={TAB_SLUGS[tab.id] ?? TAB_SLUGS[DEFAULT_TAB]} />
        </div>
      ))}
    </main>
  );
};

export default DigitalToolkit;
